import { FoundationLayout } from "layouts/FoundationLayout";
import { PublicLayout } from "layouts/PublicLayout";
import { RestrictAreaLayout } from "layouts/RestrictAreaLayout";
import { WelcomeLayout } from "layouts/WelcomeLayout";
import React, { lazy } from "react";
import { createBrowserRouter, createRoutesFromElements, isRouteErrorResponse, Navigate, Route, useRouteError } from "react-router";
import { DashboardPaths, ReportsRouter } from "routes/DashboardRouter";
import { FoundationPaths, FoundationRouter } from "routes/FoundationRouter";
import { IntegrationPaths, IntegrationRouter } from "routes/IntegrationRouter";
import { KnowledgePaths, KnowledgeRouter } from "routes/KnowledgeRouter";
import { LmsPaths, LmsRouter } from "routes/LmsRouter";
import { SubscriptionsPaths } from "routes/SubscriptionsRouter";
import { EditOwnProfile } from "views/foundation/user/Home/EditProfile";
import EditProfile from "views/foundation/user/Home/EditProfile/EditProfile";
import { EditUserProfileInterest } from "views/foundation/user/Home/EditProfile/EditUserProfileInterest";
import { FullChannelSearch } from "views/general/GlobalSearch/FullChannelSearch";
import { FullGlobalSearch } from "views/general/GlobalSearch/FullGlobalSearch";
import { FullTopicSearch } from "views/general/GlobalSearch/FullTopicSearch";
import { FullUserSearch } from "views/general/GlobalSearch/FullUserSearch";
import { RagSearch } from "views/general/GlobalSearch/RagSearch";
import { Welcome } from "views/home";
import { FeedUserProfile } from "views/knowledge/feed/FeedUserProfile";
import { OwnProfile } from "views/knowledge/feed/FeedUserProfile/OwnProfile";
import { AccessDenied } from "views/public/AccessDenied";
import { AccountConfirmation } from "views/public/AccountConfirmation";
import { AccountResetPassword } from "views/public/AccountResetPassword";
import { LoginPage } from "views/public/LoginPage";
import { PageNotFound } from "views/public/PageNotFound";
import { ReleaseNotes } from "views/public/ReleaseNotes";
import { ServerNotAvailable } from "views/public/ServerNotAvailable";
import { SpyLoginPage } from "views/public/SpyLoginPage";
import { TestPage } from "views/TestPage";

// const Overview = lazy(() => import('./pages/Overview'));
// const Subscription = lazy(() => import('./pages/Subscription'));

// import the router from the subscriptions micro frontend
const SubscriptionsRouter = lazy(() => import("./SubscriptionsRouter"));

class Paths {
    static readonly FullGlobalSearchPrefix = "/FullGlobalSearch";
    static readonly FullGlobalSearchVideo = "Video";
    static readonly FullGlobalSearchDocument = "Document";
    static readonly FullGlobalSearchTraining = "Training";
}

export class RootPaths {
    static readonly Home = "/Welcome";
    static readonly Login = "/Login";
    static readonly SpyLogin = "/SpyLogin/:authorizationId";
    static readonly SpyLoginRequestAuthorization = "/SpyLoginRequestAuthorization/:authorizationId";
    static readonly LoginExpired = "/Login?expired=1";
    static readonly AccountConfirmation = "/AccountConfirmation/:securityStamp";
    static readonly AccountResetPassword = "/AccountResetPassword/:securityStamp";
    static readonly ServerNotAvailable = "/ServerNotAvailable";
    static readonly AccessDenied = "/AccessDenied";
    static readonly PageNotFound = "/PageNotFound";
    static readonly NoAuthorized = "/NoAuthorized";
    static readonly ReleaseNotes = "/ReleaseNotes";
    static readonly EditSelfProfile = "/EditSelfProfile";
    static readonly SelfProfile = "/SelfProfile";
    static readonly UserProfile = "/UserProfile";
    static readonly EditProfile = "/EditProfile";
    static readonly UserProfileInterest = "/UserProfileInterest";

    static readonly FullUserSearch = "/FullUserSearch";
    static readonly FullTopicSearch = "/FullTopicSearch";
    static readonly FullChannelSearch = "/FullChannelSearch";
    static readonly FullGlobalSearch = Paths.FullGlobalSearchPrefix;
    static readonly FullTraining = Paths.FullGlobalSearchPrefix + "/Trainings";
    static readonly RagSearch = "/RagSearch";

    static BuildUserProfile(id?: string) { return RootPaths.UserProfile + (id ? "/" + id : ""); }
    static BuildEditProfile(id: string) { return RootPaths.EditProfile + "/" + id; }
    static BuildSearchTab(tab?: "Trainings" | "Videos" | "Documents") { return Paths.FullGlobalSearchPrefix + (tab ? "/" + tab : "") + document.location.search; }
    static BuildSpyLogin(id: string) { return RootPaths.SpyLogin.replace(":authorizationId", id); }
}

/**
 * Esta funcção é apenas uma bypass para detecção de erro do ReactRouter.
 * O componente mais externo "<ErrorBoundary/>" irá interpretar os erros que venham a ocorrer.
 * @returns
 */
function BypassReactRouterErrorBoundary() {
    const error = useRouteError();
    if (isRouteErrorResponse(error)) {
        return (
            <div>
                <h1>Router Response Error</h1>
                <h2>{error.status}</h2>
                <h3>{error.data}</h3>
            </div>
        );
    }

    throw error;
}

/**
 * Rotas.
 * @param rootChildren
 * @returns
 */
const Root = (rootChildren: React.ReactNode) => (
    <Route element={rootChildren} errorElement={<BypassReactRouterErrorBoundary />}>
        <Route element={<PublicLayout />}>
            <Route path="/" element={<Navigate to={RootPaths.Home} />} />
            <Route path={RootPaths.Login} element={<LoginPage />} />
            <Route path={RootPaths.SpyLogin} element={<SpyLoginPage />} />
            <Route path={RootPaths.AccessDenied} element={<AccessDenied />} />
            <Route path={RootPaths.AccountConfirmation} element={<AccountConfirmation />} />
            <Route path={RootPaths.AccountResetPassword} element={<AccountResetPassword />} />
            <Route path={RootPaths.ServerNotAvailable} element={<ServerNotAvailable />} />
            <Route path={RootPaths.PageNotFound} element={<PageNotFound />} />
            <Route path="*" element={<PageNotFound />} />
        </Route>
        <Route element={<FoundationLayout />}>
            <Route path={`${FoundationPaths.Prefix}/*`} element={<FoundationRouter />} />
            <Route path={`${SubscriptionsPaths.Prefix}/*`} element={<SubscriptionsRouter />} />
        </Route>
        <Route element={<WelcomeLayout />}>
            <Route path={RootPaths.Home} element={<Welcome />} />
            <Route path={RootPaths.ReleaseNotes} element={<ReleaseNotes />} />
            <Route path={RootPaths.EditSelfProfile} element={<EditOwnProfile />} />

            <Route path={RootPaths.FullGlobalSearch} >
                <Route index element={<FullGlobalSearch />} />
                <Route path=":tab" element={<FullGlobalSearch />} />
            </Route>
            <Route path={RootPaths.FullChannelSearch} element={<FullChannelSearch />} />
            <Route path={RootPaths.FullTopicSearch} element={<FullTopicSearch />} />
            <Route path={RootPaths.FullUserSearch} element={<FullUserSearch />} />

            <Route path={RootPaths.SelfProfile} element={<OwnProfile />} />

            <Route path={RootPaths.UserProfile}>
                <Route index element={<OwnProfile />} />
                <Route path=":id" element={<FeedUserProfile />} />
            </Route>

            <Route path={RootPaths.EditProfile}>
                <Route path=":id" element={<EditProfile />} />
            </Route>

            <Route path={RootPaths.UserProfileInterest} element={<EditUserProfileInterest />} />
        </Route>

        <Route element={<RestrictAreaLayout />}>
            <Route path={RootPaths.RagSearch} element={<RagSearch />} />
        </Route>

        <Route path={`${KnowledgePaths.Prefix}/*`} element={<KnowledgeRouter />} />
        <Route path={`${LmsPaths.Prefix}/*`} element={<LmsRouter />} />
        <Route path={`${DashboardPaths.Prefix}/*`} element={<ReportsRouter />} />
        <Route path={`${IntegrationPaths.Prefix}/*`} element={<IntegrationRouter />} />

        <Route path="/TestPage" element={<TestPage />} />
    </Route>
);

/**
 * Root Router
 * @param rootChildren
 * @returns
 */
export const RootRouter = (rootChildren: React.ReactNode) => createBrowserRouter(createRoutesFromElements(Root(rootChildren)));
